<template>
  <div ref="viewer"></div>
</template>

<script>
import WebViewer from '@pdftron/webviewer';
import { Auth, graphqlOperation, API } from 'aws-amplify';

import { annotationsByProjectDocumentID } from '@/graphql/queries';
import {
  createUserAnnotations,
  updateUserAnnotations,
} from '@/graphql/mutations';

import {
  PDFTRON_ADD_ACTION,
  PDFTRON_DELETE_ACTION,
  PDFTRON_MODIFY_ACTION,
} from '../constants/constants';
import { getIdFromAnnotation } from '../utils/pdfTronUtils';
import { mapGetters } from 'vuex';
import { getCurUser } from '../constants/fromLocalStorageConstants.js';
import { logError } from '@/utils/loggerUtils';

export default {
  name: 'Webviewer',
  props: {
    path: String,
    src: String,
    selectedDocPageNum: Number,
    entities: Array,
    searchTerm: {
      type: String,
      default: '*',
    },
    docName: String,
    documentID: String,
    editContext: String,
    extractingData: Boolean,
    extractDataAnnotationsMap: Map,
    isInProject: { type: Boolean, default: true },
  },
  emits: [
    'annotationsSaving',
    'annotationsSaved',
    'extractAnnotationChanged',
    'extractDataAnnotationsChanged',
    // 'downloadingTable',
    // 'downloadedTable',
    // 'tableNotDetected',
  ],
  data() {
    return {
      userInfo: null,
      hasAnnotations: undefined,
      annotationID: undefined,
    };
  },
  computed: {
    ...mapGetters(['projectID']),
    searchTermArray() {
      const arr = this.searchTerm
        .replace(' AND ', ' ')
        .replace(' OR ', ' ')
        .replace(' NOT ', ' ')
        // eslint-disable-next-line no-useless-escape
        .replace(/[\(\)\|\+]|(\s+\-)/g, ' ')
        .split(/(".*?"|[^"\s]+)(?=\s*|\s*$)/g)
        .filter((a) => a !== undefined && a.trim().length > 0)
        .map((a) =>
          a.startsWith('"') && a.endsWith('"')
            ? a.substring(1, a.length - 1)
            : a
        );
      return arr;
    },
    annotationsMap: {
      get() {
        return this.extractDataAnnotationsMap;
      },
      set(newMap) {
        this.$emit('extractDataAnnotationsChanged', newMap);
      },
    },
  },
  watch: {
    entities() {
      this.documentViewer.trigger('entitiesUpdated');
    },
  },
  async created() {
    this.visitedPages = new Set();
    this.visitedPages.add(this.selectedDocPageNum);

    if (!this.isInProject) {
      this.hasAnnotations = false;
      return;
    }

    this.userInfo = await Auth.currentUserInfo();
    //check if this document in this document already has annotations
    const variables = {
      projectID: this.projectID,
      documentID: { beginsWith: this.documentID },
    };
    const response = await API.graphql(
      graphqlOperation(annotationsByProjectDocumentID, variables)
    );
    if (response.data.annotationsByProjectDocumentID.items.length === 0) {
      //has no previous annotations
      this.hasAnnotations = false;
    } else {
      //has previous annotations
      this.hasAnnotations = true;
      this.annotationID =
        response.data.annotationsByProjectDocumentID.items[0].id;
    }
  },
  mounted() {
    WebViewer(
      {
        disableFlattenedAnnotations: true,
        path: this.path,
        initialDoc: this.src,
        licenseKey: process.env.VUE_APP_PDFTRON_LICENSE_KEY,
        annotationUser: getCurUser().email,
      },
      this.$refs.viewer
    ).then((instance) => {
      const { documentViewer, annotationManager, Annotations } = instance.Core;

      this.documentViewer = documentViewer;
      // image cropping
      const Tools = instance.Tools;
      // const Annotations = instance.Annotations;
      const docViewer = instance.docViewer;
      // const annotManager = instance.annotManager;

      const ImageSelectTool = function () {
        Tools.RectangleCreateTool.apply(this, arguments);
      };
      ImageSelectTool.prototype = new Tools.RectangleCreateTool();

      const getPageCanvas = function (pageIndex) {
        return instance.iframeWindow.document.querySelector(
          '#pageContainer' + pageIndex + ' .canvas' + pageIndex
        );
      };

      // const globalThis = this;

      docViewer.on('documentLoaded', function () {
        const imageSelectToolName = 'ImageSelect';
        const imageSelectTool = new ImageSelectTool(docViewer);

        instance.registerTool({
          toolName: imageSelectToolName,
          toolObject: imageSelectTool,
          buttonImage: 'assets/extract-csv-icon.png',
          buttonName: 'imageSelectToolButton',
          tooltip: 'Extract Table',
        });

        instance.setHeaderItems(function (header) {
          const imageSelectButton = {
            type: 'toolButton',
            toolName: imageSelectToolName,
            dataElement: 'imageSelectToolButton',
          };
          header.push(imageSelectButton);
        });

        imageSelectTool.on('annotationAdded', function (annotation) {
          const pageIndex = annotation.PageNumber;
          // get the canvas for the page
          const pageCanvas = getPageCanvas(pageIndex);
          const topOffset = parseFloat(pageCanvas.style.top) || 0;
          const leftOffset = parseFloat(pageCanvas.style.left) || 0;
          const zoom =
            docViewer.getZoom() *
            instance.iframeWindow.utils.getCanvasMultiplier();

          const x = annotation.X * zoom - leftOffset;
          const y = annotation.Y * zoom - topOffset;
          const width = annotation.Width * zoom;
          const height = annotation.Height * zoom;

          const copyCanvas = document.createElement('canvas');
          copyCanvas.width = width;
          copyCanvas.height = height;
          const ctx = copyCanvas.getContext('2d');
          // copy the image data from the page to a new canvas so we can get the data URL
          ctx.drawImage(pageCanvas, x, y, width, height, 0, 0, width, height);

          // This is not required, but maybe we should save the rectangle
          // (not the image) in a different color to indicate that the table
          // has been extracted
          // create a new stamp annotation that will have the image data that was under the rectangle
          // const stampAnnot = new Annotations.StampAnnotation();
          // stampAnnot.PageNumber = annotation.PageNumber;
          // stampAnnot.X = annotation.X;
          // stampAnnot.Y = annotation.Y;
          // stampAnnot.Width = annotation.Width;
          // stampAnnot.Height = annotation.Height;
          // stampAnnot.Author = annotManager.getCurrentUser();
          // stampAnnot.setImageData(copyCanvas.toDataURL());

          copyCanvas.toBlob(
            (blob) => {
              const formData = new FormData();
              formData.append('file', blob, 'table-image.png');
              fetch(
                'https://tables.cedience.com/extract' /* api_url in your case */,
                {
                  method: 'POST',
                  body: formData,
                }
              )
                .then((r) => r.json())
                .then((d) => {
                  // logMessage(d);
                  // this.downloadData();
                  let csvContent = 'data:text/csv;charset=utf-8,';
                  if (d.tables && d.tables.length > 0) {
                    // globalThis.$emit('downloadedTable', false);
                    // globalThis.$emit('tableNotDetected', false);
                    // globalThis.$emit('downloadingTable', true);
                    d.tables.map((table) => {
                      const table_rows = [];
                      table.map((row) => {
                        table_rows.push(row);
                      });
                      csvContent += table_rows
                        .map(
                          (row) =>
                            row
                              .map(String) // convert every value to String
                              .map((v) => v.replaceAll('"', '""')) // escape double colons
                              .map((v) => `"${v}"`) // quote it
                              .join(',') // comma-separated
                        )
                        .join('\r\n'); // rows starting on new lines
                      // .join('\n')
                      // .replace(/(^\[)|(\]$}{')/gm, '');
                      csvContent += [''];
                    });

                    const csvData = encodeURI(csvContent);
                    const link = document.createElement('a');
                    link.setAttribute('href', csvData);
                    link.setAttribute('download', 'extracted_table.csv');
                    link.click();
                    // globalThis.$emit('tableNotDetected', false);
                    // globalThis.$emit('downloadingTable', false);
                    // globalThis.$emit('downloadedTable', true);
                  } else {
                    // globalThis.$emit('downloadedTable', false);
                    // globalThis.$emit('downloadingTable', true);
                    // globalThis.$emit('tableNotDetected', true);
                  }
                });
            },
            'image/png',
            0.9
          );

          // annotManager.addAnnotation(stampAnnot);
          // annotManager.selectAnnotation(stampAnnot);
          // we don't need the rectangle anymore so we can remove it
          // annotManager.deleteAnnotation(annotation);
        });
      });

      // basic features

      // if (!this.isInProject) {
      //   instance.UI.disableTools();
      //   instance.UI.disableFeatures();
      // }
      //function for merging annotations with pdf file and downloading
      document.getElementById('download-button').onclick = async () => {
        await instance.UI.downloadPdf({
          includeAnnotations: true,
          flatten: true,
          filename: this.docName,
        });
      };

      documentViewer.addEventListener('documentLoaded', async () => {
        if (!this.isInProject) {
          documentViewer.displayPageLocation(this.selectedDocPageNum, 0, 0);
          documentViewer.zoomTo(1.25);
          // documentViewer.setFitMode(documentViewer.FitMode.FitWidth);
          return;
        }
        //load all the annoations saved by user in db
        const xfdfString = await this.loadAnnotationsFromDatabase(
          this.documentID
        );
        await instance.Core.annotationManager.importAnnotations(xfdfString);

        //Make query words highlighted
        const queue = this.searchTermArray;
        // const entities = this.entities;
        const annotationsList = [];
        function drawAnnotation(phrase, currentPage, entity = false) {
          return new Promise((resolve) => {
            const searchMode =
              instance.Core.Search.Mode.PAGE_STOP |
              instance.Core.Search.Mode.HIGHLIGHT |
              instance.Core.Search.Mode.REGEX |
              instance.Core.Search.Mode.WHOLE_WORD;
            const searchOptions = {
              fullSearch: true,
              startPage: currentPage,
              endPage: currentPage,
              onResult: (result) => {
                if (
                  result.resultCode === instance.Core.Search.ResultCode.FOUND
                ) {
                  const textQuad = result.quads[0].getPoints();
                  const annot = new Annotations.TextHighlightAnnotation();
                  annot.X = textQuad.x1;
                  annot.Width = textQuad.x2 - textQuad.x1;
                  annot.PageNumber = result.pageNum;
                  annot.Y = textQuad.y3;
                  annot.Height = textQuad.y1 - textQuad.y3;
                  annot.FillColor = new Annotations.Color(0, 0, 255, 0.5);
                  annot.StrokeColor = new Annotations.Color(0, 0, 255, 0.5);
                  annot.Quads = [textQuad];
                  annot.Listable = false;
                  annot.setCustomData('searchTerm', 'true');
                  annotationManager.addAnnotation(annot);
                  annotationsList.push(annot);
                  annotationManager.drawAnnotationsFromList(annotationsList);
                }
              },
              onDocumentEnd: () => {
                resolve();
              },
            };

            const entityOptions = {
              fullSearch: true,
              startPage: currentPage,
              endPage: currentPage,
              onResult: (result) => {
                if (
                  result.resultCode === instance.Core.Search.ResultCode.FOUND
                ) {
                  const textQuad = result.quads[0].getPoints();
                  const annot = new Annotations.TextHighlightAnnotation();
                  annot.X = textQuad.x1;
                  annot.Width = textQuad.x2 - textQuad.x1;
                  annot.PageNumber = result.pageNum;
                  annot.Y = textQuad.y3;
                  annot.Height = textQuad.y1 - textQuad.y3;
                  annot.FillColor = new Annotations.Color(240, 88, 37, 0.75);
                  annot.StrokeColor = new Annotations.Color(240, 88, 37, 0.75);
                  annot.Quads = [textQuad];
                  annot.Listable = false;
                  annot.setCustomData('searchTerm', 'true');
                  annotationManager.addAnnotation(annot);
                  annotationsList.push(annot);
                  annotationManager.drawAnnotationsFromList(annotationsList);
                }
              },
              onDocumentEnd: () => {
                resolve();
              },
            };

            documentViewer.textSearchInit(
              phrase,
              searchMode,
              entity ? entityOptions : searchOptions
            );
          });
        }

        function drawAnnotations(currentPage) {
          if (queue && queue.length) {
            Promise.resolve().then(() => {
              // if (entities && entities.length) {
              //   // logMessage(entities.map((e) => e.tag_text).join('|'));
              //   drawAnnotation(entities.join('|'), currentPage, true);
              // } else {
              drawAnnotation(queue.join('|'), currentPage, false);
              // }
            });
          }
        }

        //open PDF to specific page
        documentViewer.displayPageLocation(this.selectedDocPageNum, 0, 0);
        documentViewer.zoomTo(1.25);
        // documentViewer.setFitMode(documentViewer.FitMode.FitWidth);
        drawAnnotations(this.selectedDocPageNum);
      });

      // documentViewer.addEventListener('entitiesUpdated', async () => {
      //   //load all the annoations saved by user in db
      //   // const xfdfString = await this.loadAnnotationsFromDatabase(
      //   //   this.documentID
      //   // );
      //   // await instance.Core.annotationManager.importAnnotations(xfdfString);

      //   //Make query words highlighted
      //   const queue = this.searchTermArray;
      //   const entities = this.entities;
      //   const annotationsList = [];
      //   function drawAnnotation(phrase, currentPage) {
      //     return new Promise((resolve) => {
      //       const searchMode =
      //         instance.Core.Search.Mode.PAGE_STOP |
      //         instance.Core.Search.Mode.HIGHLIGHT |
      //         instance.Core.Search.Mode.REGEX |
      //         instance.Core.Search.Mode.WHOLE_WORD;

      //       const entityOptions = {
      //         fullSearch: true,
      //         startPage: currentPage,
      //         endPage: currentPage,
      //         onResult: (result) => {
      //           if (
      //             result.resultCode === instance.Core.Search.ResultCode.FOUND
      //           ) {
      //             const textQuad = result.quads[0].getPoints();
      //             const annot = new Annotations.TextHighlightAnnotation();
      //             annot.X = textQuad.x1;
      //             annot.Width = textQuad.x2 - textQuad.x1;
      //             annot.PageNumber = result.pageNum;
      //             annot.Y = textQuad.y3;
      //             annot.Height = textQuad.y1 - textQuad.y3;
      //             annot.FillColor = new Annotations.Color(240, 88, 37, 0.75);
      //             annot.StrokeColor = new Annotations.Color(240, 88, 37, 0.75);
      //             annot.Quads = [textQuad];
      //             annot.Listable = false;
      //             annot.setCustomData('searchTerm', 'true');
      //             annotationManager.addAnnotation(annot);
      //             annotationsList.push(annot);
      //             annotationManager.drawAnnotationsFromList(annotationsList);
      //           }
      //         },
      //         onDocumentEnd: () => {
      //           resolve();
      //         },
      //       };

      //       documentViewer.textSearchInit(phrase, searchMode, entityOptions);
      //     });
      //   }

      //   function drawAnnotations(currentPage) {
      //     if (queue && queue.length) {
      //       Promise.resolve().then(() => {
      //         if (entities && entities.length) {
      //           // logMessage(entities.map((e) => e.tag_text).join('|'));
      //           drawAnnotation(entities.join('|'), currentPage);
      //         }
      //       });
      //     }
      //   }

      //   //open PDF to specific page
      //   // documentViewer.displayPageLocation(this.selectedDocPageNum, 0, 0);
      //   // documentViewer.zoomTo(1.25);
      //   // documentViewer.setFitMode(documentViewer.FitMode.FitWidth);
      //   drawAnnotations(this.selectedDocPageNum);
      //   // logMessage(entities);
      // });

      // adding an event listener for when the page number has changed
      instance.Core.documentViewer.addEventListener(
        'pageNumberUpdated',
        (pageNumber) => {
          const queue = this.searchTermArray;
          // const entities = this.entities;
          const annotationsList = [];
          function drawAnnotation(phrase, currentPage, entity) {
            return new Promise((resolve) => {
              const searchMode =
                instance.Core.Search.Mode.PAGE_STOP |
                instance.Core.Search.Mode.HIGHLIGHT |
                instance.Core.Search.Mode.REGEX |
                instance.Core.Search.Mode.WHOLE_WORD;
              const searchOptions = {
                fullSearch: true,
                startPage: currentPage,
                endPage: currentPage,
                onResult: (result) => {
                  if (
                    result.resultCode === instance.Core.Search.ResultCode.FOUND
                  ) {
                    const textQuad = result.quads[0].getPoints();
                    const annot = new Annotations.TextHighlightAnnotation();
                    annot.X = textQuad.x1;
                    annot.Width = textQuad.x2 - textQuad.x1;
                    annot.PageNumber = result.pageNum;
                    annot.Y = textQuad.y3;
                    annot.Height = textQuad.y1 - textQuad.y3;
                    annot.FillColor = new Annotations.Color(0, 0, 255, 0.5);
                    annot.StrokeColor = new Annotations.Color(0, 0, 255, 0.5);
                    annot.Quads = [textQuad];
                    annot.Listable = false;
                    annot.setCustomData('searchTerm', 'true');
                    annotationManager.addAnnotation(annot);
                    annotationManager.drawAnnotationsFromList([annot]);
                  }
                },
                onDocumentEnd: () => {
                  resolve();
                },
              };

              const entityOptions = {
                fullSearch: true,
                startPage: currentPage,
                endPage: currentPage,
                onResult: (result) => {
                  if (
                    result.resultCode === instance.Core.Search.ResultCode.FOUND
                  ) {
                    const textQuad = result.quads[0].getPoints();
                    const annot = new Annotations.TextHighlightAnnotation();
                    annot.X = textQuad.x1;
                    annot.Width = textQuad.x2 - textQuad.x1;
                    annot.PageNumber = result.pageNum;
                    annot.Y = textQuad.y3;
                    annot.Height = textQuad.y1 - textQuad.y3;
                    annot.FillColor = new Annotations.Color(240, 88, 37, 0.75);
                    annot.StrokeColor = new Annotations.Color(
                      240,
                      88,
                      37,
                      0.75
                    );
                    annot.Quads = [textQuad];
                    annot.Listable = false;
                    annot.setCustomData('searchTerm', 'true');
                    annotationManager.addAnnotation(annot);
                    annotationsList.push(annot);
                    annotationManager.drawAnnotationsFromList(annotationsList);
                  }
                },
                onDocumentEnd: () => {
                  resolve();
                },
              };

              documentViewer.textSearchInit(
                phrase,
                searchMode,
                entity ? entityOptions : searchOptions
              );
            });
          }

          function drawAnnotations(currentPage) {
            if (queue && queue.length) {
              Promise.resolve().then(() => {
                // drawAnnotation(queue.join('|'), currentPage);
                // if (entities && entities.length) {
                //   // logMessage(entities.map((e) => e.tag_text).join('|'));
                //   drawAnnotation(entities.join('|'), currentPage, true);
                // } else {
                drawAnnotation(queue.join('|'), currentPage, false);
                // }
              });
            }
          }
          if (!this.visitedPages.has(pageNumber)) {
            drawAnnotations(pageNumber);
            this.visitedPages.add(pageNumber);
          }
        }
      );

      annotationManager.addEventListener(
        'annotationChanged',
        async (annotations, action, { imported }) => {
          if (!this.isInProject) {
            return;
          }
          if (imported) return;
          else if (this.userAnnotationChanged(annotations)) {
            //save updated annotations to backend
            const userAnnotationList = instance.Core.annotationManager
              .getAnnotationsList()
              .filter(this.isUserAnnotation);

            if (this.extractingData) {
              annotations.forEach((a) => {
                switch (action) {
                  case PDFTRON_ADD_ACTION:
                    this.extractDataAnnotationsMap.set(
                      getIdFromAnnotation(a),
                      a
                    );
                    break;
                  case PDFTRON_MODIFY_ACTION:
                    this.extractDataAnnotationsMap.set(
                      getIdFromAnnotation(a),
                      a
                    );
                    break;
                  case PDFTRON_DELETE_ACTION:
                    this.extractDataAnnotationsMap.delete(
                      getIdFromAnnotation(a)
                    );
                    break;
                  default:
                    logError(
                      new Error(
                        `Unexpected pdftron action: ${action} for annotation with id ${getIdFromAnnotation(
                          a
                        )}`
                      )
                    );
                }
              });
              const extractXFDFString =
                await instance.Core.annotationManager.exportAnnotations({
                  annotList: Array.from(
                    this.extractDataAnnotationsMap.values()
                  ),
                });
              this.$emit('extractAnnotationChanged', extractXFDFString);
            }

            const xfdfString =
              await instance.Core.annotationManager.exportAnnotations({
                annotList: userAnnotationList,
              });
            this.saveAnnotationsToDatabase(this.documentID, xfdfString);
          }
        }
      );

      // adds a button to the header that on click sets the page to the next page
      instance.UI.setHeaderItems((header) => {
        header.push({
          type: 'actionButton',
          img: 'https://icons.getbootstrap.com/assets/icons/caret-right-fill.svg',
          onClick: () => {
            const currentPage = instance.Core.documentViewer.getCurrentPage();
            const totalPages = instance.Core.documentViewer.getPageCount();
            const atLastPage = currentPage === totalPages;

            if (atLastPage) {
              instance.Core.documentViewer.setCurrentPage(1);
            } else {
              instance.Core.documentViewer.setCurrentPage(currentPage + 1);
            }
          },
        });
      });
    });
    // setTimeout(() => {
    //   this.documentViewer.trigger('entitiesUpdated');
    // }, 2000);
  },
  methods: {
    async loadAnnotationsFromDatabase(documentID) {
      const variables = {
        projectID: this.projectID,
        documentID: { eq: documentID },
      };

      const response = await API.graphql(
        graphqlOperation(annotationsByProjectDocumentID, variables)
      );
      if (response.data.annotationsByProjectDocumentID.items.length > 0) {
        return response.data.annotationsByProjectDocumentID.items[0]
          .annotations;
      } else {
        return null;
      }
    },
    async saveAnnotationsToDatabase(documentID, xfdfString) {
      //check if there is already saved
      this.$emit('annotationsSaving', true);
      let response = undefined;
      if (this.hasAnnotations) {
        //have previous for this document, update annotations
        response = await API.graphql(
          graphqlOperation(updateUserAnnotations, {
            input: {
              id: this.annotationID,
              projectID: this.projectID,
              documentID: documentID,
              annotations: xfdfString,
            },
          })
        );
      } else {
        //no previous for this document, save annotations
        response = await API.graphql(
          graphqlOperation(createUserAnnotations, {
            input: {
              projectID: this.projectID,
              documentID: documentID,
              annotations: xfdfString,
              accessGroups: getCurUser().groups,
            },
          })
        );
      }

      if (response.data) {
        this.$emit('annotationsSaving', false);
        this.$emit('annotationsSaved');
      }
    },

    //helpers
    isUserAnnotation(annotation) {
      if (annotation.getCustomData('searchTerm') === 'true') return false;
      else return true;
    },
    userAnnotationChanged(annotations) {
      for (let i = 0; i < annotations.length; i++) {
        if (this.isUserAnnotation(annotations[i])) {
          return true;
        } else continue;
      }
      return false;
    },
    // downloadTable() {
    //   let csvContent = 'data:text/csv;charset=utf-8,';
    //   const headers = ['Header 1', 'Header 2', 'Header 3'];

    //   const content = [];
    //   const row1 = ['Row 1', 'Value 1', 'Value 2'];
    //   const row2 = ['Row 2', 'Value 1', 'Value 2'];
    //   content.push(row1);
    //   content.push(row2);

    //   csvContent += [headers, ...content]
    //     .join('\n')
    //     .replace(/(^\[)|(\]$}{')/gm, '');

    //   const csvData = encodeURI(csvContent);
    //   const link = document.createElement('a');
    //   link.setAttribute('href', csvData);
    //   link.setAttribute('download', 'extracted_table.csv');
    //   link.click();
    // },
  },
};
</script>

<style scoped>
div {
  width: 100%;
  height: 100%;
}
</style>
